// ModalContext.tsx
import React, {
	createContext,
	useContext,
	useState,
	ReactNode,
	useCallback,
	useRef,
} from 'react';

interface ModalContextType {
	openModal: (modalName: string, modalProps?: Record<string, any>) => void;
	closeModal: () => void; // Close the last opened modal
	getIsModalOpen: () => boolean; // Check if any modal is open
	isSpecificModalOpen: (modalName: string) => boolean; // Check if a specific modal is open
	closeAllModal: () => void; // Close all modals
	closeSpecificModal: (modalName: string) => void; // Close a specific modal
	getModalProps: <P>(modalName: string) => P | null;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [openModals, setOpenModals] = useState<string[]>([]);
	const modalPropsMap = useRef(new Map());

	const openModal = useCallback(
		(modalName: string, modalProps?: Record<string, any>) => {
			setOpenModals(prev => [...prev, modalName]);
			if (modalProps) {
				modalPropsMap.current.set(modalName, modalProps);
			}
		},
		[],
	);

	const closeModal = useCallback(() => {
		setOpenModals(prev => prev.slice(0, -1)); // Remove the last modal
	}, []);

	const closeAllModal = useCallback(() => {
		setOpenModals([]);
		modalPropsMap.current.clear();
	}, []);

	const getIsModalOpen = () => {
		return openModals.length > 0; // Check if there are any open modals
	};

	const isSpecificModalOpen = (modalName: string) => {
		return openModals.includes(modalName); // Check if the specific modal is open
	};

	const closeSpecificModal = useCallback((modalName: string) => {
		setOpenModals(prev => prev.filter(name => name !== modalName));
		modalPropsMap.current.delete(modalName);
	}, []);

	const getModalProps = useCallback(
		<P = unknown>(modalName: string): P | null => {
			if (!isSpecificModalOpen(modalName)) return null;
			return (modalPropsMap.current.get(modalName) as P) ?? null;
		},
		[isSpecificModalOpen],
	);

	return (
		<ModalContext.Provider
			value={{
				openModal,
				getModalProps,
				closeModal,
				getIsModalOpen,
				isSpecificModalOpen,
				closeAllModal,
				closeSpecificModal,
			}}
		>
			{children}
		</ModalContext.Provider>
	);
};

export const useModal = (): ModalContextType => {
	const context = useContext(ModalContext);
	if (!context) {
		throw new Error('useModal must be used within a ModalProvider');
	}

	return context;
};
